import { createServerFn } from "@tanstack/react-start";
import { getWebRequest } from "@tanstack/react-start/server";
import type { UserSelect } from "./schemas";
import { clearUserIdCookie } from "~/lib/cookies";

export interface AuthRequestStateNotSignedIn {
  isSignedIn: false;
}

export interface AuthRequestStateSignedIn {
  isSignedIn: true;
  user: UserSelect;
}

export type AuthRequestState =
  | AuthRequestStateNotSignedIn
  | AuthRequestStateSignedIn;

export function setAuthRequestState(
  sharedMap: Map<string, unknown>,
  state: AuthRequestState,
): void {
  sharedMap.set("auth", state);
}

export function getAuthRequestState(
  sharedMap: Map<string, unknown>,
): AuthRequestState {
  return (sharedMap.get("auth") ?? { isSignedIn: false }) as AuthRequestState;
}

export const signOut = createServerFn({ method: "POST" }).handler(async () => {
  const webRequest = getWebRequest();
  if (!webRequest) {
    throw new Error("Cannot sign out without an active web request");
  }

  const [{ getAuth }] = await Promise.all([
    import("@clerk/tanstack-react-start/server"),
  ]);
  const auth = await getAuth(webRequest);

  if (auth.sessionId) {
    const clerkClient = await resolveClerkClient();
    await clerkClient.sessions.revokeSession(auth.sessionId);
  }

  clearUserIdCookie();

  return { success: true } as const;
});

export type UpdateUserParams = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
};

async function resolveClerkClient() {
  const { initializeClerkIfNeeded, getClerk } = await import("./clerk-server");
  await initializeClerkIfNeeded();
  return getClerk();
}

export const updateClerkUser = createServerFn({ method: "POST" })
  .validator((data: { userId: string; params: UpdateUserParams }) => data)
  .handler(async ({ data }) => {
    const { userId, params } = data;
    const clerkClient = await resolveClerkClient();
    await clerkClient.users.updateUser(userId, params);
  });

export const updateClerkEmailAddresses = createServerFn({ method: "POST" })
  .validator((data: { emailAddressId: string }) => data)
  .handler(async ({ data }) => {
    const { emailAddressId } = data;
    const clerkClient = await resolveClerkClient();
    const response = await clerkClient.emailAddresses.updateEmailAddress(
      emailAddressId,
      {
        primary: true,
        verified: true,
      },
    );

    return response.id;
  });
